<script lang="ts" setup>
import { computed, onBeforeMount, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { accountService } from "@/app/services/AccountService";
import { giftService } from "@/app/services/GiftService";
import AppButton from "@/components/atoms/button/AppButton.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import { authStore } from "@/store/auth";
import { Gift } from "@/types/Gift";
import { formatNumber } from "@/utils/number";
import { useLoader } from "@/utils/loader";
import { useErrorModal } from "@/utils/modal";
import { useCustomRouter } from "@/utils/router";

const route = useRoute();
const router = useRouter();
const loader = useLoader();
const errorModal = useErrorModal();
const customRouter = useCustomRouter();

const giftId = ref(0);
const gift = ref<Gift>({
  id: 0,
  createdAt: "",
  updatedAt: "",
  giftName: "...",
  giftType: "goods",
  requiredPoint: 0
});

const user = computed(() => authStore.user!);
const requiredPoint = computed(() => gift.value.requiredPoint);
const point = computed(() => user.value.caisukePoint);
const afterExchangePoint = computed(() => point.value - requiredPoint.value);
const canExchange = computed(() => point.value >= requiredPoint.value);

onBeforeMount(async () => {
  loader.show();
  try {
    giftId.value = Number(route.params.giftId);

    // アカウント情報を更新
    await accountService.updateUserData();

    const result = await giftService.fetchPointGift(giftId.value);
    if (result) {
      gift.value = result;
    }
  } catch (err) {
    await errorModal.showErrorModal(err as Error);
  } finally {
    loader.hide();
  }
});
function onClickBack() {
  router.back();
}
async function requestExchangeGift() {
  loader.show();
  try {
    await giftService.requestExchange({ gift: giftId.value });
    await customRouter.pushIgnoreError({ name: "ExchangeCouponFinished" });
  } catch (err) {
    await errorModal.showErrorModal(err as Error);
  } finally {
    loader.hide();
  }
}
</script>

<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="onClickBack" />
      </template>
      交換の確認
    </Header>

    <div class="confirm-exchange-coupon p-3">
      <div class="section1">
        <div class="gift-item">
          <p class="gift-item__title text-black text-size-18 text-weight-600">
            <span>{{ gift.giftName }}</span>
          </p>
          <div class="d-flex align-items-end justify-content-between mt-3">
            <div>
              <p class="text-mild text-size-12">使用ポイント</p>
              <p
                class="text-size-18 text-weight-600"
                data-testid="required-point"
              >
                {{ formatNumber(requiredPoint) }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="section2">
        <div class="bg-white border border-outline-color px-3 mt-3 round-4">
          <div class="border-bottom border-outline-color">
            <div class="d-flex border-bottom border-outline-color py-3">
              <p class="point-label text-size-14">現ポイント</p>
              <p class="text-size-14 ml-4" data-testid="current-point">
                {{ formatNumber(point) }}pt
              </p>
            </div>
            <div class="d-flex py-3">
              <p class="point-label text-size-14">交換後ポイント</p>
              <p class="text-size-14 ml-4" data-testid="after-exchange-point">
                {{ formatNumber(afterExchangePoint) }}pt
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="section3">
        <AppButton
          block
          variant="primary"
          :disabled="!canExchange"
          @click="requestExchangeGift"
          >交換を申請</AppButton
        >
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.confirm-exchange-coupon {
  .section1 {
    .gift-item {
      padding: 14px 26px;
      margin-top: 8px;
      background: url("~@/assets/img/bg_ticket.png") no-repeat center center;
      background-size: 100% 100%;

      &:first-child {
        margin-top: 6px;
      }

      &__title span {
        background: linear-gradient(
          to top,
          #fff19a,
          #fff19a 45%,
          rgba(#fff19a, 0) 45.1%
        );
      }
    }
  }

  .section2 {
    .point-label {
      width: 104px;
    }
  }

  .section3 {
    margin-top: 40px;
  }
}
</style>
